import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Divider,
  Form,
  Header,
  Icon,
  Image,
  Modal,
} from 'semantic-ui-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/User';
import {
  API,
  getLogo,
  getSystemName,
  showError,
  showSuccess,
  showWarning,
} from '../helpers';
import { onGitHubOAuthClicked, onLarkOAuthClicked } from './utils';
import larkIcon from '../images/lark.svg';

const LoginForm = () => {
  const { t } = useTranslation();
  const [inputs, setInputs] = useState({
    username: '',
    password: '',
    wechat_verification_code: '',
  });
  const [searchParams] = useSearchParams();
  const { username, password } = inputs;
  const [, userDispatch] = useContext(UserContext);
  let navigate = useNavigate();
  const [status, setStatus] = useState({});
  const [showWeChatLoginModal, setShowWeChatLoginModal] = useState(false);
  const logo = getLogo();
  const systemName = getSystemName();

  useEffect(() => {
    if (searchParams.get('expired')) {
      showError(t('messages.error.login_expired'));
    }
    let status = localStorage.getItem('status');
    if (status) {
      status = JSON.parse(status);
      setStatus(status);
    }
  }, []);

  const onWeChatLoginClicked = () => {
    setShowWeChatLoginModal(true);
  };

  const onSubmitWeChatVerificationCode = async () => {
    const res = await API.get(
      `/api/oauth/wechat?code=${inputs.wechat_verification_code}`
    );
    const { success, message, data } = res.data;
    if (success) {
      userDispatch({ type: 'login', payload: data });
      localStorage.setItem('user', JSON.stringify(data));
      navigate('/');
      showSuccess(t('messages.success.login'));
      setShowWeChatLoginModal(false);
    } else {
      showError(message);
    }
  };

  function handleChange(e) {
    const { name, value } = e.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  }

  async function handleSubmit() {
    if (username && password) {
      const res = await API.post(`/api/user/login`, {
        username,
        password,
      });
      const { success, message, data } = res.data;
      if (success) {
        userDispatch({ type: 'login', payload: data });
        localStorage.setItem('user', JSON.stringify(data));
        if (username === 'root' && password === '123456') {
          navigate('/user/edit');
          showSuccess(t('messages.success.login'));
          showWarning(t('messages.error.root_password'));
        } else {
          navigate('/token');
          showSuccess(t('messages.success.login'));
        }
      } else {
        showError(message);
      }
    }
  }

  return (
    <>
      <div className='login-shell'>
        <section className='login-brand-panel'>
          <div className='login-brand-mark'>
            <Image src={logo} />
            <div>
              <span>AI Gateway</span>
              <strong>{systemName}</strong>
            </div>
          </div>
          <div className='login-brand-copy'>
            <h1>统一模型接入与运营控制台</h1>
            <p>
              集中管理渠道、令牌、额度与调用日志，为团队提供稳定、清晰、可追踪的
              AI API 服务入口。
            </p>
          </div>
          <div className='login-feature-list'>
            <div>
              <Icon name='shield alternate' />
              <span>安全访问</span>
            </div>
            <div>
              <Icon name='chart line' />
              <span>用量可视</span>
            </div>
            <div>
              <Icon name='sitemap' />
              <span>多渠道调度</span>
            </div>
          </div>
        </section>

        <section className='login-form-panel'>
          <Card fluid className='login-card'>
            <Card.Content>
              <Card.Header className='login-card-header'>
                <Image src={logo} />
                <Header as='h2'>
                  <Header.Content>{t('auth.login.title')}</Header.Content>
                  <Header.Subheader>
                    欢迎回来，请登录继续管理服务
                  </Header.Subheader>
                </Header>
              </Card.Header>
              <Form size='large' className='login-form'>
                <Form.Input
                  fluid
                  icon='user'
                  iconPosition='left'
                  placeholder={t('auth.login.username')}
                  name='username'
                  value={username}
                  onChange={handleChange}
                  autoComplete='username'
                />
                <Form.Input
                  fluid
                  icon='lock'
                  iconPosition='left'
                  placeholder={t('auth.login.password')}
                  name='password'
                  type='password'
                  value={password}
                  onChange={handleChange}
                  autoComplete='current-password'
                />
                <Button
                  fluid
                  size='large'
                  className='login-submit-button'
                  onClick={handleSubmit}
                >
                  {t('auth.login.button')}
                </Button>
              </Form>

              {(status.github_oauth ||
                status.wechat_login ||
                status.lark_client_id) && (
                <>
                  <Divider horizontal className='login-oauth-divider'>
                    {t('auth.login.other_methods')}
                  </Divider>
                  <div className='login-oauth-actions'>
                    {status.github_oauth && (
                      <Button
                        circular
                        color='black'
                        icon='github'
                        onClick={() =>
                          onGitHubOAuthClicked(status.github_client_id)
                        }
                      />
                    )}
                    {status.wechat_login && (
                      <Button
                        circular
                        color='green'
                        icon='wechat'
                        onClick={onWeChatLoginClicked}
                      />
                    )}
                    {status.lark_client_id && (
                      <div
                        className='login-lark-button'
                        onClick={() =>
                          onLarkOAuthClicked(status.lark_client_id)
                        }
                      >
                        <Image src={larkIcon} avatar />
                      </div>
                    )}
                  </div>
                </>
              )}
            </Card.Content>
          </Card>
        </section>
      </div>
      <div className='login-page-footer'>
        <span>© 2026 中软智能科技. All Rights Reserved. </span>
        <a
          href='https://github.com/songquanpeng/one-api'
          target='_blank'
          rel='noreferrer'
          className='app-footer-link'
        >
          oneapi
        </a>
      </div>

      <Modal
        onClose={() => setShowWeChatLoginModal(false)}
        onOpen={() => setShowWeChatLoginModal(true)}
        open={showWeChatLoginModal}
        size={'mini'}
      >
        <Modal.Content>
          <Modal.Description>
            <Image src={status.wechat_qrcode} fluid />
            <div style={{ textAlign: 'center' }}>
              <p>{t('auth.login.wechat.scan_tip')}</p>
            </div>
            <Form size='large'>
              <Form.Input
                fluid
                placeholder={t('auth.login.wechat.code_placeholder')}
                name='wechat_verification_code'
                value={inputs.wechat_verification_code}
                onChange={handleChange}
              />
              <Button
                fluid
                size='large'
                className='login-submit-button'
                onClick={onSubmitWeChatVerificationCode}
              >
                {t('auth.login.button')}
              </Button>
            </Form>
          </Modal.Description>
        </Modal.Content>
      </Modal>
    </>
  );
};

export default LoginForm;
